import { fetchWithChatFeedback } from './chatFeedbackRequired';
/**
 * SOW Verification API Functions
 * Handles document upload, caching, and verification
 */

import {
  extractDetailMessage,
  normalizeApiError,
  USER_FACING_ERROR_MESSAGES,
} from '../utils/userFacingError';
import {
  submitManualEditAiRequest,
  getManualEditAiHistory,
  type ManualEditAiHistory,
  type ManualEditAiRequest,
  type ManualEditAiResult,
} from './manualEditAi';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// The Construction upload endpoint intentionally returns a human-readable
// HTTPException detail for a corrupt or misnamed DOCX.  Direct-fetch callers
// must turn that known contract into canonical UI copy; all other details stay
// behind `toUserFacingError` so raw server diagnostics never reach JSX.
const INVALID_SOW_DOCX_UPLOAD_DETAIL =
  /^The uploaded (?:SOW|Requirements|MACC reference|SOW Template|A-E Guide) DOCX is invalid\./;

function isInvalidSowDocxUploadDetail(detail: unknown): boolean {
  return typeof detail === 'string' && INVALID_SOW_DOCX_UPLOAD_DETAIL.test(detail);
}

export interface SOWDocumentInfo {
  filename: string;
  file_type: string;
  size: number;
  modified_at: string;
}

export interface SOWDocuments {
  sow?: SOWDocumentInfo;
  requirements?: SOWDocumentInfo;
  macc?: SOWDocumentInfo;
  sow_template?: SOWDocumentInfo;
  /**
   * Existing-condition drawings supplied alongside the proposed design set.
   * Legacy `drawings` uploads remain the proposed/new drawing source.
   */
  as_built_drawings?: SOWDocumentInfo;
  drawings?: SOWDocumentInfo;
  ae_guide?: SOWDocumentInfo;
}

/** Current planned-execution choices accepted by the vehicle-context write API. */
export type SOWPlannedVehicle =
  | 'saber'
  | 'macc'
  | 'other';

/** Historic persisted values that remain readable but cannot be newly written. */
export type SOWHistoricalPlannedVehicle =
  | 'other_idiq'
  | 'standalone_contract'
  | 'unknown';

/** A read-compatible stored vehicle value; historic values are never coerced on load. */
export type SOWStoredPlannedVehicle = SOWPlannedVehicle | SOWHistoricalPlannedVehicle;

export type SOWVehicleDocumentRole =
  | 'unclassified'
  | 'base_vehicle_contract'
  | 'ordering_instructions'
  | 'task_order_requirement'
  | 'pricing_mechanism'
  | 'project_evidence'
  | 'local_process'
  | 'reference_background'
  | 'closeout_record';

export type SOWVehicleEvidenceLevel =
  | 'controlling_term'
  | 'project_evidence'
  | 'reference_background';

export type SOWVehicleInclusionStatus =
  | 'included'
  | 'unclassified'
  | 'unavailable'
  | 'excluded';

export interface SOWVehicleDocument {
  id: string;
  source_id: string;
  filename: string;
  size: number;
  readable: boolean;
  analysis_status: 'ready' | 'unavailable';
  analysis_message: string;
  content_fingerprint: string;
  role: SOWVehicleDocumentRole;
  document_role: SOWVehicleDocumentRole;
  evidence_level: SOWVehicleEvidenceLevel;
  effective_date_or_version?: string | null;
  locator?: string | null;
  status?: string;
  user_confirmed: boolean;
  inclusion_status: SOWVehicleInclusionStatus;
  /** `null` is the backend serializer's explicit absence for ordinary documents. */
  legacy_named_slot?: 'macc' | null;
  results_stale: boolean;
}

export interface SOWVehicleContext {
  planned_vehicle: SOWStoredPlannedVehicle | null;
  work_path?: string | null;
  vehicle_documents: SOWVehicleDocument[];
  revision?: number;
  fingerprint?: string | null;
  /** Legacy compatibility field returned by older backend revisions. */
  legacy?: boolean;
  /** Backend-resolved policy used for a current or historical stored value. */
  effective_vehicle_policy?: SOWPlannedVehicle | null;
  /** True when `planned_vehicle` is one of the preserved historical literals. */
  historical_vehicle_value?: boolean;
  results_stale?: boolean;
}

export interface SOWVehicleContextUpdate {
  planned_vehicle: SOWPlannedVehicle;
  work_path?: string | null;
}

export interface SOWVehicleDocumentUpdate {
  role: SOWVehicleDocumentRole;
  evidence_level: SOWVehicleEvidenceLevel;
  effective_date_or_version?: string;
  locator?: string;
}

function vehicleContextError(
  payload: unknown,
  fallback: string,
  fallbackErrorCode?: string,
): Error {
  const normalized = normalizeApiError(payload);
  const error = new Error(normalized.message || fallback) as Error & {
    error_code?: string;
    technical_detail?: string;
  };
  error.error_code = normalized.errorCode || fallbackErrorCode;
  error.technical_detail = normalized.technicalDetail;
  return error;
}

function dispatchVehicleContextAuthExpired(response: Response): void {
  if (response.status === 401) {
    window.dispatchEvent(new Event('auth-expired'));
  }
}

const STORED_VEHICLE_VALUES = new Set<SOWStoredPlannedVehicle>([
  'saber',
  'macc',
  'other',
  'other_idiq',
  'standalone_contract',
  'unknown',
]);
const VEHICLE_DOCUMENT_ROLES = new Set<SOWVehicleDocumentRole>([
  'unclassified',
  'base_vehicle_contract',
  'ordering_instructions',
  'task_order_requirement',
  'pricing_mechanism',
  'project_evidence',
  'local_process',
  'reference_background',
  'closeout_record',
]);
const VEHICLE_EVIDENCE_LEVELS = new Set<SOWVehicleEvidenceLevel>([
  'controlling_term',
  'project_evidence',
  'reference_background',
]);
const VEHICLE_INCLUSION_STATUSES = new Set<SOWVehicleInclusionStatus>([
  'included',
  'unclassified',
  'unavailable',
  'excluded',
]);
const SHA256_PATTERN = /^[0-9a-f]{64}$/;

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function invalidVehicleResponse(): Error {
  return new Error('Invalid vehicle-context response');
}

function optionalText(value: unknown): boolean {
  return value === undefined || value === null || typeof value === 'string';
}

export function parseSOWVehicleDocument(
  value: unknown,
  expectedSourceId?: string,
): SOWVehicleDocument {
  if (!isRecord(value)) throw invalidVehicleResponse();
  const id = value.id;
  const sourceId = value.source_id;
  const role = value.role;
  const documentRole = value.document_role;
  if (
    typeof id !== 'string'
    || id.length === 0
    || sourceId !== id
    || (expectedSourceId !== undefined && id !== expectedSourceId)
    || typeof value.filename !== 'string'
    || value.filename.length === 0
    || !Number.isInteger(value.size)
    || (value.size as number) < 0
    || typeof role !== 'string'
    || !VEHICLE_DOCUMENT_ROLES.has(role as SOWVehicleDocumentRole)
    || documentRole !== role
    || typeof value.evidence_level !== 'string'
    || !VEHICLE_EVIDENCE_LEVELS.has(
      value.evidence_level as SOWVehicleEvidenceLevel,
    )
    || typeof value.readable !== 'boolean'
    || !['ready', 'unavailable'].includes(String(value.analysis_status))
    || typeof value.analysis_message !== 'string'
    || typeof value.content_fingerprint !== 'string'
    || !SHA256_PATTERN.test(value.content_fingerprint)
    || typeof value.user_confirmed !== 'boolean'
    || typeof value.inclusion_status !== 'string'
    || !VEHICLE_INCLUSION_STATUSES.has(
      value.inclusion_status as SOWVehicleInclusionStatus,
    )
    || typeof value.results_stale !== 'boolean'
    || !optionalText(value.effective_date_or_version)
    || !optionalText(value.locator)
    || (value.legacy_named_slot !== undefined
      && value.legacy_named_slot !== null
      && value.legacy_named_slot !== 'macc')
  ) {
    throw invalidVehicleResponse();
  }
  return value as unknown as SOWVehicleDocument;
}

export function parseSOWVehicleContext(value: unknown): SOWVehicleContext {
  if (!isRecord(value) || !Array.isArray(value.vehicle_documents)) {
    throw invalidVehicleResponse();
  }
  const plannedVehicle = value.planned_vehicle;
  const effectivePolicy = value.effective_vehicle_policy;
  if (
    !(plannedVehicle === null
      || (typeof plannedVehicle === 'string'
        && STORED_VEHICLE_VALUES.has(plannedVehicle as SOWStoredPlannedVehicle)))
    || !optionalText(value.work_path)
    || !Number.isInteger(value.revision)
    || (value.revision as number) < 0
    || !(value.fingerprint === null
      || (typeof value.fingerprint === 'string'
        && SHA256_PATTERN.test(value.fingerprint)))
    || typeof value.legacy !== 'boolean'
    || !(effectivePolicy === null
      || effectivePolicy === 'saber'
      || effectivePolicy === 'macc'
      || effectivePolicy === 'other')
    || typeof value.historical_vehicle_value !== 'boolean'
    || typeof value.results_stale !== 'boolean'
  ) {
    throw invalidVehicleResponse();
  }
  const vehicleDocuments = value.vehicle_documents.map((document) => (
    parseSOWVehicleDocument(document)
  ));
  if (new Set(vehicleDocuments.map((document) => document.id)).size
    !== vehicleDocuments.length) {
    throw invalidVehicleResponse();
  }
  return {
    ...(value as unknown as SOWVehicleContext),
    vehicle_documents: vehicleDocuments,
  };
}

/** Read the persisted vehicle context that later SOW stages use to build a run. */
export async function getSOWVehicleContext(flowId: string): Promise<SOWVehicleContext> {
  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/sow-verification/${flowId}/vehicle-context`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
  });
  dispatchVehicleContextAuthExpired(response);
  if (!response.ok) {
    throw vehicleContextError(await response.json().catch(() => ({})), 'Failed to get planned contract vehicle');
  }
  return parseSOWVehicleContext(await response.json());
}

/** Persist a deliberate vehicle selection in stage-200 storage. */
export async function saveSOWVehicleContext(
  flowId: string,
  context: SOWVehicleContextUpdate,
): Promise<SOWVehicleContext> {
  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/sow-verification/${flowId}/vehicle-context`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(context),
    credentials: 'include',
  });
  dispatchVehicleContextAuthExpired(response);
  if (!response.ok) {
    throw vehicleContextError(await response.json().catch(() => ({})), 'Failed to save planned contract vehicle');
  }
  return parseSOWVehicleContext(await response.json());
}

/** Add one optional governing-vehicle source without replacing sibling sources. */
export async function uploadSOWVehicleDocument(
  flowId: string,
  file: File,
): Promise<SOWVehicleDocument> {
  const formData = new FormData();
  formData.append('file', file);
  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/sow-verification/${flowId}/vehicle-documents`, {
    method: 'POST',
    body: formData,
    credentials: 'include',
  });
  dispatchVehicleContextAuthExpired(response);
  if (!response.ok) {
    const fallbackErrorCode = response.status === 413
      ? 'reupload_document'
      : [400, 422].includes(response.status)
        ? 'invalid_docx_upload'
        : undefined;
    throw vehicleContextError(
      await response.json().catch(() => ({})),
      'Failed to upload governing vehicle document',
      fallbackErrorCode,
    );
  }
  return parseSOWVehicleDocument(await response.json());
}

/** Atomically replace one vehicle source while preserving its server identity. */
export async function replaceSOWVehicleDocument(
  flowId: string,
  sourceId: string,
  file: File,
): Promise<SOWVehicleDocument> {
  const formData = new FormData();
  formData.append('file', file);
  const response = await fetchWithChatFeedback(
    `${API_BASE_URL}/api/sow-verification/${flowId}/vehicle-documents/${sourceId}/content`,
    {
      method: 'PUT',
      body: formData,
      credentials: 'include',
    },
  );
  dispatchVehicleContextAuthExpired(response);
  if (!response.ok) {
    const fallbackErrorCode = response.status === 413
      ? 'reupload_document'
      : [400, 422].includes(response.status)
        ? 'invalid_docx_upload'
        : undefined;
    throw vehicleContextError(
      await response.json().catch(() => ({})),
      'Failed to replace governing vehicle document',
      fallbackErrorCode,
    );
  }
  return parseSOWVehicleDocument(await response.json(), sourceId);
}

/** Confirm the user-selected role/evidence metadata for one vehicle source. */
export async function updateSOWVehicleDocument(
  flowId: string,
  sourceId: string,
  update: SOWVehicleDocumentUpdate,
): Promise<SOWVehicleDocument> {
  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/sow-verification/${flowId}/vehicle-documents/${sourceId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(update),
    credentials: 'include',
  });
  dispatchVehicleContextAuthExpired(response);
  if (!response.ok) {
    throw vehicleContextError(await response.json().catch(() => ({})), 'Failed to save vehicle-document metadata');
  }
  return parseSOWVehicleDocument(await response.json(), sourceId);
}

/** Remove one vehicle source only; named SOW/support uploads are untouched. */
export async function deleteSOWVehicleDocument(
  flowId: string,
  sourceId: string,
): Promise<{ success: boolean; results_stale?: boolean }> {
  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/sow-verification/${flowId}/vehicle-documents/${sourceId}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
  });
  dispatchVehicleContextAuthExpired(response);
  if (!response.ok) {
    throw vehicleContextError(await response.json().catch(() => ({})), 'Failed to delete governing vehicle document');
  }
  const result: unknown = await response.json();
  const payload = result as Record<string, unknown>;
  if (
    !result
    || typeof result !== 'object'
    || payload.success !== true
    || typeof payload.results_stale !== 'boolean'
  ) {
    throw new Error(
      'The server did not confirm that the governing vehicle document was deleted',
    );
  }
  return result as { success: true; results_stale: boolean };
}

/** Apply one immediate, server-validated AI edit batch to a Construction SOW. */
export function applyConstructionManualEditAi(
  flowId: string,
  request: ManualEditAiRequest,
): Promise<ManualEditAiResult> {
  return submitManualEditAiRequest(
    `/api/sow-verification/${flowId}/manual-edit-ai`,
    request,
  );
}

/** Read the durable, display-only AI edit transcript for Construction Manual Edit. */
export function getConstructionManualEditAiHistory(flowId: string): Promise<ManualEditAiHistory> {
  return getManualEditAiHistory(`/api/sow-verification/${flowId}/manual-edit-ai/history`);
}

export interface SOWUploadResponse {
  success: boolean;
  file_path: string;
  file_size: number;
  file_type: string;
}

export interface SOWHistoryBrokenRecord {
  index: number;
  checkpoint_id: string;
  reason: string;
}

export interface SOWHistoryIntegrityResult {
  status: 'verified' | 'broken';
  verified: boolean;
  algorithm: 'SHA-256';
  record_count: number;
  chain_record_count: number;
  legacy_record_count: number;
  fingerprint: string | null;
  protected_since: string | null;
  baseline_created: boolean;
  broken_record: SOWHistoryBrokenRecord | null;
}

/** Verify the stored SOW checkpoint documents and their recorded metadata. */
export async function verifySOWHistoryIntegrity(
  flowId: string,
): Promise<SOWHistoryIntegrityResult> {
  const response = await fetchWithChatFeedback(
    `${API_BASE_URL}/api/sow-verification/${flowId}/history-integrity/verify`,
    {
      method: 'POST',
      credentials: 'include',
    },
  );
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    const normalized = normalizeApiError(payload);
    const error = new Error(normalized.message) as Error & {
      error_code?: string;
      technical_detail?: string;
    };
    error.error_code = normalized.errorCode;
    error.technical_detail = normalized.technicalDetail;
    throw error;
  }
  return response.json();
}

/**
 * Get user email from localStorage for authentication headers
 */
function getUserEmail(): string {
  return localStorage.getItem('userEmail') || (window as any).userEmail || '';
}

/**
 * Upload a document for SOW verification (stores on backend)
 * @param flowId - The flow/session identifier
 * @param fileType - Type of file
 * @param file - The file to upload
 * @returns Upload response with file path and size
 */
export async function uploadSOWDocument(
  flowId: string,
  fileType: string,
  file: File
): Promise<SOWUploadResponse> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('flow_id', flowId);
  formData.append('file_type', fileType);

  const headers: Record<string, string> = {};

  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/sow-verification/upload-document`, {
    method: 'POST',
    headers,
    body: formData,
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
    if (isInvalidSowDocxUploadDetail(error.detail)) {
      throw new Error(USER_FACING_ERROR_MESSAGES.invalid_docx_upload);
    }
    const detail = extractDetailMessage(error.detail, `Upload failed with status ${response.status}`);
    throw new Error(detail);
  }

  return response.json();
}

/**
 * Get list of uploaded SOW verification documents for a flow
 * @param flowId - The flow/session identifier
 * @returns Object containing info about uploaded documents
 */
export async function getSOWDocuments(flowId: string): Promise<SOWDocuments> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/sow-verification/${flowId}/documents`, {
    method: 'GET',
    headers,
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to get documents' }));
    const detail = extractDetailMessage(error.detail, `Failed to get documents with status ${response.status}`);
    throw new Error(detail);
  }

  return response.json();
}

/**
 * Delete a specific SOW verification document
 * @param flowId - The flow/session identifier
 * @param fileType - Type of file to delete
 */
export async function deleteSOWDocument(
  flowId: string,
  fileType: string
): Promise<{ success: boolean }> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/sow-verification/${flowId}/documents/${fileType}`, {
    method: 'DELETE',
    headers,
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Delete failed' }));
    const detail = extractDetailMessage(error.detail, `Delete failed with status ${response.status}`);
    throw new Error(detail);
  }

  return response.json();
}

/**
 * Start SOW verification with uploaded documents
 * @param flowId - The flow/session identifier
 * @returns Job ID for tracking verification progress
 */
export async function startSOWVerification(
  flowId: string
): Promise<{ job_id: string }> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/sow-verification/run`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ flow_id: flowId }),
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to start verification' }));
    const detail = extractDetailMessage(error.detail, `Failed to start verification with status ${response.status}`);
    throw new Error(detail);
  }

  return response.json();
}
